import styled from "styled-components";
import { useEffect, useRef, useState, } from "react";
import { useParams, } from "react-router-dom";
import { performManagedReload, recordError, } from "@bracket/catch";
import { FlexBox, Loader, } from "@bracket/ui";
import * as G from "@globals";
import { usePlatform, usePricing, useUI, useVaults, useWallet, } from "@bracket/platform/store";
import { getTheme, } from "@bracket/ui/themes";
import { logger, }from "@bracket/ui/logger";
import { BracketLogo, } from "@bracket/ui/logos";



export interface BaseLoaderProps {
	children: React.ReactNode;
}



export const BaseLoader = ({ children, }: BaseLoaderProps) => {


	const { address, } = G.wallet.getBracketWallet();
	const { symbol, } = useParams();
	const { ready: walletReady, } = useWallet();
	const { ready: vaultsReady, } = useVaults({ vault:symbol, });
	const { ready: platformReady, } = usePlatform();
	const { revealed, setRevealed, } = useUI();
	const { ready: pricingReady, } = usePricing();
	const timeoutRef = useRef<NodeJS.Timeout|null>(null);

	const [ completed, setCompleted ] = useState(false);

	useEffect(() => {

		if(!revealed && (!walletReady || !vaultsReady || !platformReady || !pricingReady)) {

			if(timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			const timer = { since: Date.now(), interval: 15000, };
			const cache_key = "base_loader_interval";

			try {

				const cached = globalThis.JSON.parse(cache_key);
				if(cached && Date.now() - cached.since > 60 * 60 * 1000) {
					timer.since = cached.since;
					timer.interval = cached.interval;
				}

			} catch(error) {
				// do nothing, use the current time
			}

			timeoutRef.current = setTimeout(() => {

				if(!revealed) {

					if(timeoutRef.current) {
						clearTimeout(timeoutRef.current);
						timeoutRef.current = null;
					}

					// logger.warn("base loader would refresh at this point ---- refresh disabled.");

					const unready = Object.entries({
						wallet: walletReady,
						vaults: vaultsReady,
						platform: platformReady,
						pricing: pricingReady,
					}).reduce((acc, curr) => curr[1] ? acc : [...acc, curr[0]], [] as string[]);

					const error = new Error("BaseLoader has timed out waiting for store readiness: (unready: " + unready.join(", ") + ")");
					const { address, anonymous, } = G.wallet.getBracketWallet();
					recordError({ error, system: "UI::BaseLoader", address: anonymous ? "0xANON" : address, });
					globalThis.localStorage.setItem(cache_key, JSON.stringify({ interval: timer.interval * 2, since: Date.now(), }));
					performManagedReload(error, 10);

				}

			}, timer.interval);

			const body = globalThis.document.querySelector("body");
			if(body) {
				body.style.overflow = "hidden";
				return;
			}

			throw new Error("BaseLoader could not find the body element");

		}
		
		return () => {
			if(timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};

	}, [ walletReady, vaultsReady, platformReady, revealed, pricingReady ]);

	useEffect(() => {
		if(!revealed && walletReady && vaultsReady && platformReady) {
			if(timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			const body = globalThis.document.querySelector("body");
			if(body) {
				body.style.overflow = "visible";
				setRevealed(true);
				setTimeout(() => { setCompleted(true); }, 2000);
				return;
			}
			throw new Error("BaseLoader could not find the body element");
		}
	}, [ walletReady, vaultsReady, platformReady, revealed, pricingReady ]);


	return (
		<Container $ready={revealed}>
			{ !completed && (
				<Splash $opacity={revealed ? 0 : 1} $address={address}>
					<Frame>
						<BracketLogo scale={4} />
						<Loader className="loader" center />
					</Frame>
				</Splash>
			)}
			{ children }
		</Container>
	);


};

const Container = styled(FlexBox)<{ $ready: boolean; }>`
	position: relative;
  min-width: 100%;
	max-width: 100%;
	width: 100%;
`;

const Splash = styled(FlexBox)<{ $opacity?: number; $address?: string; }>`
	position: absolute;
	min-height: 100%;
	z-index: 99;
	width: 100%;
	height: 100%;
	opacity: ${({ $address, $opacity }) => $address && $opacity || 0};
	background-color: ${ () => getTheme().background };
	transition: opacity 0.5s ease-in-out;
	pointer-events: none;

	> div {
		scale: .75;
		margin-top: 80px;
	}

`;

export const Frame = styled(FlexBox)`
	flex-direction: column;
	justify-content: center;
	height: 300px;
	color: ${ () => getTheme().surface_on };
	align-items: center;
`;
